import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarRange, FileSpreadsheet, GraduationCap, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/components/OrderFormButton";
import { api, formatPrice } from "@/lib/api";

const GRANS = [["month", "Mois"], ["quarter", "Trimestre"], ["year", "Année"]];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function periodOptions(gran) {
  const now = new Date();
  const y = now.getFullYear();
  const out = [];
  if (gran === "month") {
    for (let i = 0; i < 24; i += 1) {
      const d = new Date(y, now.getMonth() - i, 1);
      out.push([`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, `${MONTHS[d.getMonth()]} ${d.getFullYear()}`]);
    }
  } else if (gran === "quarter") {
    let q = Math.floor(now.getMonth() / 3) + 1;
    let yy = y;
    for (let i = 0; i < 12; i += 1) {
      out.push([`${yy}-T${q}`, `T${q} ${yy}`]);
      q -= 1;
      if (q === 0) { q = 4; yy -= 1; }
    }
  } else {
    for (let i = 0; i < 6; i += 1) out.push([String(y - i), String(y - i)]);
  }
  return out;
}

const pill = (active) => `rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${active ? "bg-amber-deep text-white" : "text-bark-soft hover:text-amber-deep"}`;
const eur = (c) => formatPrice(Math.round(c));

export default function TurnoverPeriods() {
  const [gran, setGran] = useState("month");
  const options = useMemo(() => periodOptions(gran), [gran]);
  const [period, setPeriod] = useState(options[0][0]);
  const [basis, setBasis] = useState("order");
  const [data, setData] = useState(null);

  const chooseGran = (g) => { setGran(g); setPeriod(periodOptions(g)[0][0]); };
  useEffect(() => {
    setData(null);
    api.get("/admin/stats/turnover", { params: { granularity: gran, period, date_basis: basis } })
      .then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [gran, period, basis]);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/stats/turnover/export", { params: { granularity: gran, period, date_basis: basis }, responseType: "blob" });
      downloadBlob(res.data, `ca_${period}.xlsx`);
    } catch { toast.error("Export impossible"); }
  };

  const s = data?.summary;
  const delta = s && s.previous_total_cents > 0 ? Math.round(((s.total_cents - s.previous_total_cents) / s.previous_total_cents) * 100) : null;

  return (
    <div data-testid="turnover-periods" className="space-y-5 rounded-3xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-serif text-xl font-semibold text-bark"><CalendarRange size={20} className="text-honey" /> Chiffre d'affaires par période</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-sand p-1">
            {GRANS.map(([k, l]) => <button key={k} data-testid={`turnover-gran-${k}`} onClick={() => chooseGran(k)} className={pill(gran === k)}>{l}</button>)}
          </div>
          <select data-testid="turnover-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-full border border-amber-900/20 bg-cream px-4 py-2 text-sm font-medium text-bark outline-none focus:border-amber-deep">
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="turnover-export" onClick={exportXlsx} className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark hover:border-amber-deep hover:text-amber-deep"><FileSpreadsheet size={15} /> Export Excel</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-bark-soft">
        <span>Commandes école datées par :</span>
        <div className="flex rounded-full bg-sand p-1">
          <button data-testid="turnover-basis-order" onClick={() => setBasis("order")} className={pill(basis === "order")}>Date de commande</button>
          <button data-testid="turnover-basis-payment" onClick={() => setBasis("payment")} className={pill(basis === "payment")}>Date de validation du paiement</button>
        </div>
      </div>

      {data === null ? (
        <p className="flex items-center justify-center gap-2 py-16 text-bark-soft"><Loader2 size={20} className="animate-spin text-amber-deep" /> Calcul…</p>
      ) : data === false ? (
        <p className="py-10 text-center text-sm text-terra">Chiffre d'affaires indisponible.</p>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-amber-deep p-5 text-white">
              <p className="text-xs uppercase tracking-[0.15em] text-white/80">Total · {data.label}</p>
              <p data-testid="period-total" className="mt-2 font-serif text-3xl font-semibold">{eur(s.total_cents)}</p>
              {delta !== null && (
                <p data-testid="period-delta" className="mt-1 flex items-center gap-1 text-xs text-white/85">
                  {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {delta >= 0 ? "+" : ""}{delta} % vs période précédente
                </p>
              )}
            </div>
            <div className="rounded-2xl border border-amber-800/10 bg-cream p-5">
              <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">CA du site</p>
              <p data-testid="period-site" className="mt-2 font-serif text-3xl font-semibold text-bark">{eur(s.site_cents)}</p>
            </div>
            <div className="rounded-2xl border border-amber-800/10 bg-cream p-5">
              <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">CA des écoles (net)</p>
              <p data-testid="period-school-net" className="mt-2 font-serif text-3xl font-semibold text-bark">{eur(s.school_net_cents)}</p>
              <p className="mt-1 text-xs text-bark-soft">{eur(s.school_gross_cents)} de ventes − {eur(s.school_commission_cents)} de commission</p>
            </div>
          </div>

          <div data-testid="turnover-chart" className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series.map((p) => ({ name: p.label, Site: p.site_cents / 100, "Écoles (net)": p.school_net_cents / 100 }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6DCCB" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6B5A4A" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B5A4A" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v} €`} width={60} />
                <Tooltip formatter={(v) => `${Number(v).toFixed(2).replace(".", ",")} €`} cursor={{ fill: "rgba(184,115,10,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Site" stackId="ca" fill="#B8730A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Écoles (net)" stackId="ca" fill="#7C9A6B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <SchoolRanking schools={data.schools} label={data.label} />
        </>
      )}
    </div>
  );
}

function SchoolRanking({ schools, label }) {
  return (
    <div data-testid="school-ranking">
      <p className="mb-2 flex items-center gap-2 font-serif text-lg font-semibold text-bark"><GraduationCap size={18} className="text-honey" /> Classement des écoles · {label}</p>
      {schools.length === 0 ? (
        <p data-testid="school-ranking-empty" className="rounded-xl bg-cream py-6 text-center text-sm text-bark-soft">Aucune vente école payée sur cette période.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead><tr className="border-b border-amber-900/10 text-xs uppercase tracking-wider text-stone-mute">
              <th className="py-2 pr-3">#</th><th className="py-2 pr-3">École</th><th className="py-2 pr-3">Commandes</th><th className="py-2 pr-3 text-right">Ventes</th><th className="py-2 pr-3 text-right">Commission reversée</th><th className="py-2 text-right">Net</th>
            </tr></thead>
            <tbody className="divide-y divide-amber-900/10">
              {schools.map((r, i) => (
                <tr key={r.school_id} data-testid={`school-rank-${r.school_id}`}>
                  <td className="py-2.5 pr-3 font-serif text-lg font-semibold text-amber-deep">{i + 1}</td>
                  <td className="py-2.5 pr-3 font-medium text-bark">{r.name}</td>
                  <td className="py-2.5 pr-3 text-bark-soft">{r.orders}</td>
                  <td className="py-2.5 pr-3 text-right text-bark">{eur(r.gross_cents)}</td>
                  <td className="py-2.5 pr-3 text-right text-terra">−{eur(r.commission_cents)}</td>
                  <td className="py-2.5 text-right font-semibold text-bark">{eur(r.net_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
